import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Brain, Users } from 'lucide-react';
import Logo from './shared/Logo';

const navLinks = [
  { label: 'Products', href: '/products', hasDropdown: true },
  { label: 'About', href: '/about' },
  { label: 'Clients', href: '/clients' },
];

const productDropdown = [
  { label: 'AI Engineering', desc: 'Software engineers powered by AI', icon: Brain, color: '#2563eb' },
  { label: 'AI Teams', desc: 'Engineers + AI agents working together', icon: Users, color: '#7c3aed' },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '0 24px',
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(15, 23, 42, 0.06)' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <div style={{
        maxWidth: 1200,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', marginTop: 20 }}>
          <Logo size={600} />
        </Link>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 32,
        }}>
          {navLinks.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <div
                key={link.href}
                style={{ position: 'relative' }}
                onMouseEnter={() => link.hasDropdown && setDropdownOpen(true)}
                onMouseLeave={() => link.hasDropdown && setDropdownOpen(false)}
              >
                <Link
                  to={link.href}
                  className="nav-link-desktop"
                  style={{
                    fontSize: '0.88rem',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? 'var(--accent-blue)' : 'var(--text-secondary)',
                    transition: 'color 0.2s',
                    letterSpacing: '0.3px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-blue)')}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.color = 'var(--text-secondary)';
                  }}
                >
                  {link.label}
                  {link.hasDropdown && (
                    <ChevronDown size={14} style={{
                      transition: 'transform 0.2s',
                      transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                    }} />
                  )}
                  {isActive && (
                    <motion.div
                      layoutId="nav-indicator"
                      style={{
                        position: 'absolute',
                        bottom: -4,
                        left: 0,
                        right: 0,
                        height: 2,
                        background: 'var(--gradient-blue)',
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Link>

                {link.hasDropdown && dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: -20,
                      paddingTop: 12,
                      zIndex: 100,
                    }}
                  >
                    <div style={{
                      background: 'white',
                      borderRadius: 16,
                      padding: '8px',
                      boxShadow: '0 8px 40px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',
                      border: '1px solid var(--border-color)',
                      width: 300,
                    }}>
                      {productDropdown.map((item, i) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={i}
                            to="/products"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: '12px 14px',
                              borderRadius: 12,
                              transition: 'background 0.2s',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            <div style={{
                              width: 40, height: 40, borderRadius: 10,
                              background: `${item.color}0a`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                              <Icon size={20} color={item.color} />
                            </div>
                            <div>
                              <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{item.label}</div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.desc}</div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}

          <Link
            to="/contact"
            className="nav-link-desktop"
            style={{
              padding: '10px 24px',
              background: 'var(--gradient-blue)',
              borderRadius: 100,
              fontSize: '0.85rem',
              fontWeight: 600,
              color: 'white',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(37, 99, 235, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Free Diagnostic
          </Link>
        </div>

        <button
          className="mobile-menu-btn"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ display: 'none', background: 'none', color: 'var(--text-primary)', padding: 8 }}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            position: 'absolute', top: 72, left: 0, right: 0,
            background: 'rgba(255, 255, 255, 0.98)', backdropFilter: 'blur(20px)',
            padding: '24px', display: 'flex', flexDirection: 'column', gap: 16,
            borderBottom: '1px solid var(--border-color)', boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          }}
        >
          {navLinks.map((link) => (
            <Link key={link.href} to={link.href}
              style={{ fontSize: '1rem', color: location.pathname === link.href ? 'var(--accent-blue)' : 'var(--text-secondary)', padding: '8px 0', fontWeight: location.pathname === link.href ? 600 : 400 }}
            >{link.label}</Link>
          ))}
          <Link to="/contact" style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            padding: '12px 24px', background: 'var(--gradient-blue)', borderRadius: 12, fontWeight: 600, color: 'white', marginTop: 8,
          }}>Free Diagnostic</Link>
        </motion.div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .nav-link-desktop { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </motion.nav>
  );
};

export default Navbar;
